/**
 * An error message wrapper with illustration.
 *
 * This can be used for a full-page error (if the whole page is broken) or for
 * a section of the page (if a specific component on the page is broken).
 * Full-page errors should use the `fillScreen` prop to quickly add adequate
 * vertical padding.
 *
 * Consider using this component any time we make a server-side fetch. This
 * component is designed to work offline, so if a user loses internet access
 * while using the product, we can show a user-friendly message that asks
 * them to try again or check their internet connectivity.
 */
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";

import BrainYarnError from "./brain-yarn-error.tsx";

interface Props {
    /**
     * The error message to show the user.
     */
    children: React.ReactNode;
    /**
     * Whether or not this is a full-page error and therefore needs extra
     * vertical padding that wouldn't make sense if the error message is in
     * a smaller section of the page.
     */
    fillScreen?: boolean;
}

// The height of the page header
const HEADER_HEIGHT = 60;

export const ErrorMessageWrapper = ({children, fillScreen = true}: Props) => {
    return (
        <View
            style={[
                {
                    margin: "0 auto",
                    maxWidth: 400,
                    alignItems: "center",
                    justifyContent: "center",
                },
                fillScreen && {
                    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                },
            ]}
        >
            <BrainYarnError />
            <View style={{height: spacing.large_24}} />
            {children}
        </View>
    );
};
